import { SelectProps } from '@round/ui-kit';
import { FieldHookConfig, useField } from 'formik';
import { OptionTypeBase, ValueType } from 'react-select';
import Select from 'ui-new/Select/Select';
import styles from 'ui-new/formik/FormField.module.css';
import cn from 'classnames';
import ErrorMessage from './ErrorMessage';

type Props<T extends OptionTypeBase, IsMulti extends boolean = false> = {
    label: string;
    className?: string;
} & Omit<SelectProps<T, IsMulti>, 'value' | 'onChange'> &
    FieldHookConfig<ValueType<T, IsMulti>>;

const SelectField = <T extends OptionTypeBase, IsMulti extends boolean = false>({
    label,
    disabled,
    className,
    isMulti,
    options,
    onMenuScrollToBottom,
    onMenuOpen,
    onMenuClose,
    inputValue,
    onInputChange,
    filterOption,
    isLoading,
    placeholder,
    components,
    formatOptionLabel,
    styles: stylesConfig,
    ...props
}: Props<T, IsMulti>) => {
    const [field, meta, helpers] = useField(props);

    const hasError = meta.error;

    return (
        <div className={cn(styles.field, className, { [styles.disabled]: disabled })}>
            <label className={styles.label} htmlFor={props.name}>
                {label}
            </label>
            <Select
                inputId={field.name}
                name={field.name}
                isMulti={isMulti}
                value={field.value}
                styles={{
                    control: (base) => ({
                        ...base,
                        border: hasError ? '1px solid #FF5050' : base.border,
                    }),
                    ...stylesConfig,
                }}
                onChange={(value: ValueType<T, IsMulti>) => {
                    helpers.setTouched(true);
                    helpers.setValue(value);
                }}
                options={options}
                onBlur={field.onBlur}
                onMenuScrollToBottom={onMenuScrollToBottom}
                onMenuOpen={onMenuOpen}
                onMenuClose={onMenuClose}
                inputValue={inputValue}
                onInputChange={onInputChange}
                filterOption={filterOption}
                isLoading={isLoading}
                placeholder={placeholder}
                components={components}
                isDisabled={disabled}
                formatOptionLabel={formatOptionLabel}
            />
            <ErrorMessage name={props.name} />
        </div>
    );
};

export default SelectField;
